import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, FileText, PenLine, Star } from "lucide-react";
import { useSeo } from "@/context/SiteContext";
import { PageHero } from "@/components/public/Shared";
import { API, api, errMsg, fmtPrice } from "@/lib/api";
import { Link, currentIntl, useT } from "@/i18n";

const longDate = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(currentIntl(), { day: "numeric", month: "long", year: "numeric" }) : "");
const longDateTime = (iso) => (iso ? new Date(iso).toLocaleString(currentIntl(), { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function useGuestInfo(path) {
  const t = useT();
  const [sp] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const b = sp.get("b"), tok = sp.get("t");
  const reload = () => api.get(`/public/stay/bookings/${b}/${path}`, { params: { t: tok } }).then((r) => setInfo(r.data)).catch(() => setErr(t("Réservation introuvable.")));
  useEffect(() => { reload(); }, [b, tok]); // eslint-disable-line react-hooks/exhaustive-deps
  return { info, err, b, tok, reload };
}

// Signature électronique du contrat en 1 clic
export function ContractSign() {
  const t = useT();
  const { info, err, b, tok, reload } = useGuestInfo("contract/info");
  const [name, setName] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  useSeo({ title: t("Signature du contrat"), index: false });
  useEffect(() => { if (info?.guest_name) setName(info.guest_name); }, [info?.guest_name]);
  const pdfUrl = `${API}/public/stay/bookings/${b}/contract.pdf?t=${encodeURIComponent(tok || "")}`;
  const sign = async (e) => {
    e.preventDefault();
    if (!accept) return toast.error(t("Merci de cocher « Lu et approuvé » pour signer"));
    setBusy(true);
    try { await api.post(`/public/stay/bookings/${b}/contract/sign`, { full_name: name, accept }, { params: { t: tok } }); toast.success(t("Contrat signé, merci !")); reload(); }
    catch (e2) { toast.error(t(errMsg(e2))); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHero eyebrow={t("Réservation")} title={t("Signature du contrat")} testId="contract-sign-hero" />
      <div className="container-site py-14 max-w-2xl" data-testid="contract-sign">
        {!info && !err && <p className="text-center text-charcoal/60">{t("Chargement…")}</p>}
        {err && <p className="text-center text-red-600" data-testid="contract-sign-error">{err}</p>}
        {info && (
          <div className="card p-6 sm:p-8 space-y-5">
            <div>
              <p className="eyebrow mb-1">{info.ref}</p>
              <p className="font-serif text-2xl text-forest">{info.apartment_name}</p>
              <p className="text-charcoal/70 mt-1">{longDate(info.start)} → {longDate(info.end)} · {t("{n} personne(s)", { n: info.guests })}</p>
              {info.quote?.total ? <p className="text-charcoal/70 text-sm mt-1">{t("Montant total : {amount}", { amount: fmtPrice(info.quote.total) })}</p> : null}
            </div>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn-secondary w-full justify-center" data-testid="contract-sign-read"><FileText size={16} /> {info.signed ? t("Télécharger le contrat signé") : t("Lire le contrat (PDF)")}</a>
            {info.signed ? (
              <p className="flex items-start gap-2 text-sage font-medium" data-testid="contract-signed-ok"><CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                {t("Contrat signé électroniquement par {name} le {date}.", { name: info.signed.name, date: longDateTime(info.signed.at) })}</p>
            ) : !info.active ? (
              <p className="text-charcoal/70" data-testid="contract-sign-inactive">{t("Le contrat pourra être signé une fois la réservation confirmée.")}</p>
            ) : (
              <form onSubmit={sign} className="space-y-4 border-t border-sand pt-5">
                <div><label className="label" htmlFor="sign-name">{t("Nom et prénom du signataire")}</label>
                  <input id="sign-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required minLength={3} data-testid="contract-sign-name" /></div>
                <label className="flex items-start gap-3 text-sm cursor-pointer">
                  <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="w-5 h-5 accent-forest mt-0.5" data-testid="contract-sign-accept" />
                  <span>{t("J'ai lu le contrat de location et je l'accepte (« Lu et approuvé »).")}</span>
                </label>
                <p className="text-xs text-charcoal/55">{t("En cliquant sur « Signer », vous signez électroniquement ce contrat. La date, l'heure et votre adresse IP sont enregistrées comme preuve, et le contrat signé vous est envoyé par e-mail.")}</p>
                <button type="submit" disabled={busy} className="btn-primary w-full py-4" data-testid="contract-sign-submit"><PenLine size={16} /> {busy ? t("Signature…") : t("Signer le contrat")}</button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Avis voyageur après le séjour (lien de l'e-mail J+1)
export function LeaveReview() {
  const t = useT();
  const { info, err, b, tok, reload } = useGuestInfo("review/info");
  const [f, setF] = useState({ first_name: "", rating: 0, comment: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useSeo({ title: t("Laisser un avis"), index: false });
  useEffect(() => { if (info?.first_name) setF((x) => ({ ...x, first_name: info.first_name })); }, [info?.first_name]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) return toast.error(t("Merci de choisir une note"));
    setBusy(true);
    try { await api.post(`/public/stay/bookings/${b}/review`, f, { params: { t: tok } }); setDone(true); reload(); }
    catch (e2) { toast.error(t(errMsg(e2))); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHero eyebrow={t("Avis voyageurs")} title={t("Laisser un avis")} testId="review-hero" />
      <div className="container-site py-14 max-w-2xl" data-testid="leave-review">
        {!info && !err && <p className="text-center text-charcoal/60">{t("Chargement…")}</p>}
        {err && <p className="text-center text-red-600" data-testid="review-error">{err}</p>}
        {info && (done || info.already ? (
          <div className="card p-8 text-center" data-testid="review-thanks">
            <CheckCircle2 size={40} className="text-sage mx-auto mb-4" />
            <p className="font-serif text-2xl text-forest">{t("Merci pour votre avis !")}</p>
            <p className="text-charcoal/70 mt-2">{t("Il sera publié sur le site après relecture.")}</p>
            <Link to="/avis-voyageurs" className="btn-secondary mt-6">{t("Voir les avis voyageurs")}</Link>
          </div>
        ) : !info.active ? (
          <p className="card p-8 text-center text-charcoal/70" data-testid="review-unavailable">{t("Ce séjour ne peut pas recevoir d'avis.")}</p>
        ) : !info.can_review ? (
          <p className="card p-8 text-center text-charcoal/70" data-testid="review-not-yet">{t("Vous pourrez laisser un avis après votre séjour")}.</p>
        ) : (
          <form onSubmit={submit} className="card p-6 sm:p-8 space-y-5">
            <p className="text-charcoal/70">{info.apartment_name} · {longDate(info.start)} → {longDate(info.end)}</p>
            <div>
              <p className="label">{t("Votre note")}</p>
              <div className="flex gap-1" role="radiogroup" aria-label={t("Votre note")}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" role="radio" aria-checked={f.rating === n} aria-label={t("{n} sur 5", { n })} onClick={() => setF({ ...f, rating: n })}
                    className="p-1" data-testid={`review-star-${n}`}>
                    <Star size={30} className={n <= f.rating ? "fill-amber-400 text-amber-400" : "text-charcoal/25"} />
                  </button>
                ))}
              </div>
            </div>
            <div><label className="label" htmlFor="rv-name">{t("Prénom")}</label>
              <input id="rv-name" className="input" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required data-testid="review-first-name" /></div>
            <div><label className="label" htmlFor="rv-comment">{t("Votre commentaire")}</label>
              <textarea id="rv-comment" rows={6} className="input" value={f.comment} minLength={10} onChange={(e) => setF({ ...f, comment: e.target.value })} required
                placeholder={t("Racontez-nous votre séjour : le chalet, l'accueil, la région…")} data-testid="review-comment" /></div>
            <button type="submit" disabled={busy} className="btn-primary w-full py-4" data-testid="review-submit">{busy ? t("Envoi…") : t("Envoyer mon avis")}</button>
          </form>
        ))}
      </div>
    </div>
  );
}
